import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrandIndexService } from '../../../services/brand-index.service';


@Component({
  selector: 'app-content-generation-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './content-generation-popup.component.html',
  styleUrl: './content-generation-popup.component.scss'
})
export class ContentGenerationPopupComponent implements OnInit { // OnChanges
  @Input() recommendation: any;
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();

  isLoading: boolean = false;
  detailedContent: any = null;
  error: string | null = null;

  // getLoading(): boolean {
  //   return this.isLoading;
  // }

  constructor(private brandIndexService: BrandIndexService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void { }

  // Reset all data when popup opens
  private resetData(): void {
    // console.log('🔄 Resetting popup data...');
    this.isLoading = false;
    this.error = null;
    this.detailedContent = null;

    // Force change detection after reset
    this.cdr.detectChanges();

    // console.log('✅ Popup data reset complete');
  }

  // Public method to manually reset data
  public resetPopupData(): void {
    // console.log('🧹 Manual reset requested...');
    this.resetData();
  }

  // Watch for isVisible changes and reset data when popup opens
  ngOnChanges(changes: any): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      // console.log('🎯 Popup opened, resetting data...');
      this.resetData();
    }
  }

  generateContent(): void {
    if (!this.recommendation) return;

    // console.log('🚀 generateContent called with recommendation:', this.recommendation);

    // Clear previous content and set loading state
    this.isLoading = true;
    this.cdr.markForCheck();
    this.error = null;
    this.detailedContent = null;

    // Force change detection for loading state
    this.cdr.detectChanges();

    // console.log('🔄 Loading state set to:', this.isLoading);

    const request = {
      university_name: this.recommendation.university,
      category: this.recommendation.category,
      issue: this.recommendation.issue,
      current_score: this.recommendation.current_score || 0
    };

    // console.log('📤 Sending request:', request);

    this.brandIndexService.generateDetailedContent(request).subscribe({
      next: (response) => {
        // console.log('📥 Received response:', response);

        if (response && response.success && response.detailed_content) {
          this.detailedContent = response.detailed_content;
          this.cdr.markForCheck();
          // console.log('✅ Content generated successfully:', this.detailedContent);
        } else {
          console.error('❌ API response invalid:', response);
          this.error = 'Failed to generate content. Please try again.';
        }

        this.isLoading = false;
        this.cdr.markForCheck();
        // console.log('🏁 Loading finished, isLoading:', this.isLoading);
        // console.log('🏁 Final detailedContent:', this.detailedContent);

        // Force change detection after all updates
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error generating content:', error);
        this.error = 'An error occurred while generating content. Please try again.';
        this.isLoading = false;
        this.cdr.markForCheck();

        // Force change detection after error
        this.cdr.detectChanges();
      }
    });
  }

  copyContent(content: any): void {
    const textToCopy = this.formatContentForCopy(content);
    navigator.clipboard.writeText(textToCopy).then(() => {
      // console.log('Content copied to clipboard');
    });
  }

  private formatContentForCopy(content: any): string {
    let formattedText = '';

    if (content.content_overview) {
      formattedText += `Content Overview:\n${content.content_overview}\n\n`;
    }

    if (content.implementation_timeline) {
      formattedText += `Implementation Timeline:\n${content.implementation_timeline}\n\n`;
    }

    if (content.content_pieces && content.content_pieces.length > 0) {
      formattedText += 'Content Pieces:\n';
      content.content_pieces.forEach((piece: any, index: number) => {
        formattedText += `\n${index + 1}. ${piece.title}\n`;
        formattedText += `Type: ${piece.type}\n`;
        formattedText += `Content: ${piece.content}\n`;
        if (piece.target_audience) {
          formattedText += `Target Audience: ${piece.target_audience}\n`;
        }
        if (piece.platform) {
          formattedText += `Platform: ${piece.platform}\n`;
        }
        if (piece.call_to_action) {
          formattedText += `Call to Action: ${piece.call_to_action}\n`;
        }
        if (piece.seo_keywords && piece.seo_keywords.length > 0) {
          formattedText += `SEO Keywords: ${piece.seo_keywords.join(', ')}\n`;
        }
        if (piece.hashtags && piece.hashtags.length > 0) {
          formattedText += `Hashtags: ${piece.hashtags.join(' ')}\n`;
        }
        if (piece.visual_suggestions) {
          formattedText += `Visual Suggestions: ${piece.visual_suggestions}\n`;
        }
      });
    }

    if (content.success_metrics && content.success_metrics.length > 0) {
      formattedText += '\nSuccess Metrics:\n';
      content.success_metrics.forEach((metric: string) => {
        formattedText += `• ${metric}\n`;
      });
    }

    if (content.next_steps && content.next_steps.length > 0) {
      formattedText += '\nNext Steps:\n';
      content.next_steps.forEach((step: string) => {
        formattedText += `• ${step}\n`;
      });
    }

    return formattedText;
  }

  exportContent(content: any): void {
    const data = {
      recommendation: this.recommendation,
      detailed_content: content,
      generated_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `detailed-content-${this.recommendation?.category?.toLowerCase().replace(' ', '-')}-${Date.now()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  closePopup(): void {
    this.close.emit();
  }

  getPriorityColor(priority: string): string {
    switch (priority?.toLowerCase()) {
      case 'high': return 'warn';
      case 'medium': return 'accent';
      case 'low': return 'primary';
      default: return 'primary';
    }
  }

  // Manual method to force change detection
  forceChangeDetection(): void {
    // console.log('🔄 Forcing change detection...');
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  // Debug method to test variable updates
  debugVariables(): void {
    // console.log('🔍 Debug Variables:', {
    //   isLoading: this.isLoading,
    //   error: this.error,
    //   detailedContent: this.detailedContent,
    //   recommendation: this.recommendation
    // });

    // Force change detection
    this.forceChangeDetection();
  }

  // Test loading state changes
  testLoadingState(): void {
    // console.log('⚡ Testing loading state changes...');

    // Reset data first
    this.resetData();

    // Test loading state
    this.isLoading = true;
    // console.log('🔄 Set isLoading to true:', this.isLoading);
    this.cdr.detectChanges();

    // Simulate API delay
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = { test: 'Test content for debugging' };
      // console.log('🔄 Set isLoading to false and added test content:', this.isLoading, this.detailedContent);
      this.cdr.detectChanges();
    }, 2000);
  }

  // Simple loading test
  simpleLoadingTest(): void {
    // console.log('🧪 Simple loading test...');

    // Reset data first
    this.resetData();

    // Set loading
    setTimeout(() => {
      this.isLoading = true;
      // console.log('🔄 Set loading to true');
      this.cdr.detectChanges();
    }, 500);

    // Stop loading and add content
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = {
        content_overview: 'Test overview',
        content_pieces: [{ title: 'Test piece', content: 'Test content' }]
      };
      // console.log('🔄 Set loading to false and added content');
      this.cdr.detectChanges();
    }, 2000);
  }

  // Test BehaviorSubject approach
  testBehaviorSubject(): void {
    // console.log('🚀 Testing BehaviorSubject approach...');

    // Reset data first
    this.resetData();

    // Set loading
    setTimeout(() => {
      this.isLoading = true;
      // console.log('🔄 Set loading to true via BehaviorSubject');
    }, 500);

    // Stop loading and add content
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = {
        content_overview: 'BehaviorSubject Test',
        content_pieces: [{ title: 'Test piece', content: 'Test content from BehaviorSubject' }]
      };
      // console.log('🔄 Set loading to false and added content via BehaviorSubject');
    }, 2000);
  }
}
